import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { getAssistantGuides } from "api/services/chat/assistant";

import { ASSISTANT_WALKTHROUGH_QUERY_PARAM } from "../constants";
import { AssistantGuide } from "../types";
import { useAssistantWalkthroughTarget } from "./useAssistantWalkthroughTarget";

export function useAssistantWalkthrough(
  page: string | undefined,
  identity: string,
  onComplete: (guideId: string, skipped: boolean) => Promise<boolean | void>
) {
  const location = useLocation();
  const [requestedGuide, setRequestedGuide] = useState(() =>
    new URLSearchParams(location.search).get(ASSISTANT_WALKTHROUGH_QUERY_PARAM)
  );
  const [guide, setGuide] = useState<AssistantGuide>();
  const [index, setIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveFailed, setHasSaveFailed] = useState(false);
  const isSkipped = useRef(false);
  const contextVersion = useRef(0);
  const isCompleting = useRef(false);

  const step = guide?.steps[index];

  useEffect(() => {
    contextVersion.current += 1;
    isCompleting.current = false;
    setIsSaving(false);
    setHasSaveFailed(false);
    setGuide(undefined);
    setIndex(0);
    setIsCompleted(false);
    isSkipped.current = false;

    return () => {
      contextVersion.current += 1;
    };
  }, [page, identity]);

  const handleCancel = useCallback(() => {
    contextVersion.current += 1;
    isCompleting.current = false;
    setIsSaving(false);
    setHasSaveFailed(false);
    setRequestedGuide(null);
    setGuide(undefined);
    setIsCompleted(false);
    isSkipped.current = false;
  }, []);

  const handleDismissCompletion = useCallback(() => {
    setIsCompleted(false);
    isSkipped.current = false;
  }, []);

  const handleAdvance = useCallback(() => {
    if (!guide) return;

    const isLastStep = index + 1 >= guide.steps.length;

    if (isLastStep) {
      if (isCompleting.current) return;
      const context = contextVersion.current;
      isCompleting.current = true;
      setIsSaving(true);
      setHasSaveFailed(false);
      void onComplete(guide.id, isSkipped.current)
        .then((saved) => {
          if (context !== contextVersion.current) return;
          isCompleting.current = false;
          setIsSaving(false);
          if (saved === false) {
            setHasSaveFailed(true);
            return;
          }
          handleCancel();
          setIsCompleted(true);
        })
        .catch(() => {
          if (context === contextVersion.current) {
            isCompleting.current = false;
            setIsSaving(false);
            setHasSaveFailed(true);
          }
        });
      return;
    }

    setIndex((currentIndex) => currentIndex + 1);
  }, [guide, index, handleCancel, onComplete]);

  const handleSkip = useCallback(() => {
    isSkipped.current = true;
    handleAdvance();
  }, [handleAdvance]);

  const highlights = useAssistantWalkthroughTarget(step, handleAdvance);

  useEffect(() => {
    if (!requestedGuide || !page) return;

    let isActive = true;
    const requestedContext = contextVersion.current;

    const startWalkthrough = async () => {
      try {
        const eligibleGuides = await getAssistantGuides({ page });
        if (!isActive || requestedContext !== contextVersion.current) return;

        setGuide(eligibleGuides.find((item) => item.id === requestedGuide));
        setIndex(0);
        setIsCompleted(false);
        isSkipped.current = false;
      } catch {
        // The shared Apollo request layer owns error feedback.
      } finally {
        if (isActive && requestedContext === contextVersion.current) {
          setRequestedGuide(null);
        }
      }
    };

    void startWalkthrough();

    return () => {
      isActive = false;
    };
  }, [requestedGuide, page, identity]);

  const handleBack = useCallback(() => {
    if (isCompleting.current) return;
    setHasSaveFailed(false);
    setIndex((currentIndex) => Math.max(0, currentIndex - 1));
  }, []);

  const handleRestart = useCallback(() => {
    if (isCompleting.current) return;
    setHasSaveFailed(false);
    isSkipped.current = false;
    setIndex(0);
  }, []);

  const handleStart = useCallback(
    (guideId: string) => {
      handleCancel();
      setRequestedGuide(guideId);
    },
    [handleCancel]
  );

  return {
    isSaving,
    hasSaveFailed,
    handleStart,
    guide,
    step,
    index,
    highlights,
    isCompleted,
    handleAdvance,
    handleSkip,
    handleCancel,
    handleBack,
    handleRestart,
    handleDismissCompletion,
  };
}
